import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Sparkles, Trash2, Mic, MicOff, Camera, X, MoreVertical, CalendarX, MinusCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import MainLayout from "../components/MainLayout";

const ChatPage = () => {
  const { user, getAuthHeaders, API } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [dailyTotals, setDailyTotals] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [deleteDayDialogOpen, setDeleteDayDialogOpen] = useState(false);
  const [deleteFoodDialog, setDeleteFoodDialog] = useState({ open: false, mealId: null, foods: [] });
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchTodayMeals();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchTodayMeals = async () => {
    try {
      const response = await axios.get(`${API}/meals/today`, {
        headers: getAuthHeaders()
      });
      
      // Convert meals to messages
      const mealMessages = [];
      response.data.meals.reverse().forEach(meal => {
        mealMessages.push({
          id: `user-${meal.id}`,
          type: "user",
          content: meal.description,
          inputType: meal.input_type
        });
        mealMessages.push({
          id: `ai-${meal.id}`,
          type: "ai",
          mealId: meal.id,
          meal: meal,
          foods: meal.foods,
          total_calories: meal.total_calories,
          total_protein: meal.total_protein,
          total_carbs: meal.total_carbs,
          total_fat: meal.total_fat
        });
      });
      
      setMessages(mealMessages);
      setDailyTotals({
        ...response.data.totals,
        calorie_goal: response.data.goals.calories,
        remaining: response.data.goals.calories - response.data.totals.calories
      });
    } catch (error) {
      console.error("Error fetching meals:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setLoading(true);

    const userMsgId = `user-${Date.now()}`;
    setMessages(prev => [...prev, { id: userMsgId, type: "user", content: userMessage }]);

    try {
      const response = await axios.post(
        `${API}/chat`,
        { content: userMessage },
        { headers: getAuthHeaders() }
      );

      if (response.data.success) {
        const aiMsgId = `ai-${Date.now()}`;
        setMessages(prev => [...prev, {
          id: aiMsgId,
          type: "ai",
          mealId: response.data.meal.id,
          ...response.data.meal
        }]);
        setDailyTotals(response.data.daily_totals);
        toast.success("Comida registrada");
      } else {
        setMessages(prev => [...prev, {
          id: `ai-error-${Date.now()}`,
          type: "ai",
          error: true,
          content: response.data.message
        }]);
      }
    } catch (error) {
      toast.error("Error al procesar tu mensaje");
      setMessages(prev => [...prev, {
        id: `ai-error-${Date.now()}`,
        type: "ai",
        error: true,
        content: "Lo siento, hubo un error. Intenta de nuevo."
      }]);
    } finally {
      setLoading(false);
    }
  };

  // ============== AUDIO RECORDING ==============
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(chunks, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await sendAudio(audioBlob);
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      toast.info("Grabando... Habla ahora");
    } catch (error) {
      console.error("Error accessing microphone:", error);
      toast.error("No se pudo acceder al micrófono");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === "recording") {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  const sendAudio = async (audioBlob) => {
    setLoading(true);
    
    const userMsgId = `user-${Date.now()}`;
    setMessages(prev => [...prev, { 
      id: userMsgId, 
      type: "user", 
      content: "🎤 Mensaje de voz...",
      inputType: "audio"
    }]);

    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const response = await axios.post(
        `${API}/chat/audio`,
        formData,
        { 
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setMessages(prev => prev.map(msg => 
        msg.id === userMsgId 
          ? { ...msg, content: response.data.transcribed_text || "Audio analizado" }
          : msg
      ));

      if (response.data.success) {
        const aiMsgId = `ai-${Date.now()}`;
        setMessages(prev => [...prev, {
          id: aiMsgId,
          type: "ai",
          mealId: response.data.meal.id,
          ...response.data.meal
        }]);
        setDailyTotals(response.data.daily_totals);
        toast.success("Comida registrada por voz");
      } else {
        setMessages(prev => [...prev, {
          id: `ai-error-${Date.now()}`,
          type: "ai",
          error: true,
          content: response.data.message
        }]);
      }
    } catch (error) {
      toast.error("Error al procesar el audio");
      setMessages(prev => [...prev, {
        id: `ai-error-${Date.now()}`,
        type: "ai",
        error: true,
        content: "Error al procesar el audio. Intenta de nuevo."
      }]);
    } finally {
      setLoading(false);
    }
  };

  // ============== IMAGE UPLOAD ==============
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Por favor selecciona una imagen");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      setShowImageUpload(true);
    };
    reader.readAsDataURL(file);
  };

  const cancelImageUpload = () => {
    setImagePreview(null);
    setShowImageUpload(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const sendImage = async () => {
    if (!imagePreview) return;
    
    setLoading(true);
    setShowImageUpload(false);

    const userMsgId = `user-${Date.now()}`;
    setMessages(prev => [...prev, { 
      id: userMsgId, 
      type: "user", 
      content: "📷 Foto de comida",
      inputType: "image",
      imagePreview: imagePreview
    }]);

    try {
      const base64Data = imagePreview.split(',')[1];

      const response = await axios.post(
        `${API}/chat/image`,
        { image_base64: base64Data },
        { headers: getAuthHeaders() }
      );

      if (response.data.success && response.data.meal.description) {
        setMessages(prev => prev.map(msg => 
          msg.id === userMsgId 
            ? { ...msg, content: `📷 ${response.data.meal.description}` }
            : msg
        ));
      }

      if (response.data.success) {
        const aiMsgId = `ai-${Date.now()}`;
        setMessages(prev => [...prev, {
          id: aiMsgId,
          type: "ai",
          mealId: response.data.meal.id,
          ...response.data.meal
        }]);
        setDailyTotals(response.data.daily_totals);
        toast.success("Comida registrada por foto");
      } else {
        setMessages(prev => [...prev, {
          id: `ai-error-${Date.now()}`,
          type: "ai",
          error: true,
          content: response.data.message
        }]);
      }
    } catch (error) {
      toast.error("Error al procesar la imagen");
      setMessages(prev => [...prev, {
        id: `ai-error-${Date.now()}`,
        type: "ai",
        error: true,
        content: "Error al procesar la imagen. Intenta de nuevo."
      }]);
    } finally {
      setLoading(false);
      setImagePreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // ============== DELETE FUNCTIONS ==============
  const deleteMeal = async (mealId) => {
    try {
      await axios.delete(`${API}/meals/${mealId}`, {
        headers: getAuthHeaders()
      });
      toast.success("Comida eliminada");
      fetchTodayMeals();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const deleteEntireDay = async () => {
    const today = new Date().toISOString().split('T')[0];
    try {
      const response = await axios.delete(`${API}/meals/day/${today}`, {
        headers: getAuthHeaders()
      });
      toast.success(response.data.message);
      setDeleteDayDialogOpen(false);
      fetchTodayMeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar el día");
    }
  };

  const openDeleteFoodDialog = (mealId, foods) => {
    setDeleteFoodDialog({ open: true, mealId, foods });
  };

  const deleteSpecificFood = async (mealId, foodIndex) => {
    try {
      const response = await axios.delete(`${API}/meals/${mealId}/food/${foodIndex}`, {
        headers: getAuthHeaders()
      });
      toast.success(response.data.message);
      setDeleteFoodDialog({ open: false, mealId: null, foods: [] });
      fetchTodayMeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar el alimento");
    }
  };

  const progressPercentage = dailyTotals 
    ? Math.min((dailyTotals.calories / dailyTotals.calorie_goal) * 100, 100)
    : 0;

  return (
    <MainLayout>
      <div className="flex flex-col h-[calc(100vh-4rem)] max-w-4xl mx-auto" data-testid="chat-page">
        {/* Daily Progress Header */}
        {dailyTotals && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border/50 rounded-2xl p-4 mb-4 shadow-sm mx-4 mt-4 lg:mx-0 lg:mt-0"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-muted-foreground">Progreso de hoy</span>
              <div className="flex items-center gap-2">
                <span className="font-mono text-lg font-semibold" data-testid="daily-calories">
                  {Math.round(dailyTotals.calories)} / {dailyTotals.calorie_goal} kcal
                </span>
                
                {/* Delete Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="delete-menu-btn">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem 
                      onClick={() => setDeleteDayDialogOpen(true)}
                      className="text-destructive focus:text-destructive cursor-pointer"
                      data-testid="delete-day-option"
                    >
                      <CalendarX className="w-4 h-4 mr-2" />
                      Eliminar todo el día
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progressPercentage}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className={`h-full rounded-full ${
                  progressPercentage > 100 ? "bg-destructive" : 
                  progressPercentage > 80 ? "bg-accent" : "bg-primary"
                }`}
              />
            </div>
            <div className="flex justify-between mt-3 text-sm">
              <div className="text-center">
                <span className="block font-mono font-semibold text-chart-protein">{Math.round(dailyTotals.protein)}g</span>
                <span className="text-xs text-muted-foreground">Proteína</span>
              </div>
              <div className="text-center">
                <span className="block font-mono font-semibold text-chart-carbs">{Math.round(dailyTotals.carbs)}g</span>
                <span className="text-xs text-muted-foreground">Carbos</span>
              </div>
              <div className="text-center">
                <span className="block font-mono font-semibold text-chart-fat">{Math.round(dailyTotals.fat)}g</span>
                <span className="text-xs text-muted-foreground">Grasa</span>
              </div>
              <div className="text-center">
                <span className={`block font-mono font-semibold ${dailyTotals.remaining < 0 ? 'text-destructive' : 'text-primary'}`}>
                  {Math.round(dailyTotals.remaining)}
                </span>
                <span className="text-xs text-muted-foreground">Restante</span>
              </div>
            </div>
          </motion.div>
        )}

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-4 lg:px-2 space-y-4 pb-4">
          {messages.length === 0 && !loading && (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center mb-4">
                <Sparkles className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-heading text-xl font-semibold mb-2">¡Hola, {user?.name}!</h3>
              <p className="text-muted-foreground max-w-md">
                Escríbeme, envía un audio o una foto de lo que has comido y calcularé las calorías y macros.
              </p>
              <div className="mt-6 space-y-2 text-sm text-muted-foreground">
                <p>💬 Escribe: "Tostadas con aceite y café"</p>
                <p>🎤 Graba: Mantén el botón de micrófono</p>
                <p>📷 Foto: Toma una foto de tu plato</p>
              </div>
            </div>
          )}

          <AnimatePresence>
            {messages.map((message, index) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ delay: index * 0.05 }}
                className={`flex ${message.type === "user" ? "justify-end" : "justify-start"}`}
              >
                {message.type === "user" ? (
                  <div className="chat-bubble-user px-4 py-3 max-w-[80%]" data-testid="user-message">
                    {message.imagePreview && (
                      <img 
                        src={message.imagePreview} 
                        alt="Foto de comida" 
                        className="w-full max-w-[200px] rounded-lg mb-2"
                      />
                    )}
                    <p className="text-sm">
                      {message.inputType === "audio" && "🎤 "}
                      {message.inputType === "image" && "📷 "}
                      {message.content}
                    </p>
                  </div>
                ) : message.error ? (
                  <div className="chat-bubble-ai px-4 py-3 max-w-[85%]" data-testid="ai-error-message">
                    <p className="text-sm text-destructive">{message.content}</p>
                  </div>
                ) : (
                  <div className="chat-bubble-ai px-4 py-3 max-w-[85%] group" data-testid="ai-message">
                    {message.foods && message.foods.length > 0 && (
                      <>
                        <p className="text-sm font-medium mb-2">Registrado:</p>
                        <div className="space-y-1 text-sm">
                          {message.foods.map((food, i) => (
                            <p key={i}>• {food.name}: {Math.round(food.calories)} kcal ({food.quantity || `${food.grams}g`})</p>
                          ))}
                        </div>
                      </>
                    )}
                    <div className="mt-3 pt-3 border-t border-border/50 flex items-center justify-between">
                      <div>
                        <p className="font-mono text-lg font-semibold text-primary">
                          {Math.round(message.total_calories)} kcal
                        </p>
                        <p className="text-xs text-muted-foreground">
                          P: {Math.round(message.total_protein)}g | C: {Math.round(message.total_carbs)}g | G: {Math.round(message.total_fat)}g
                        </p>
                      </div>
                      
                      {/* Delete Options Menu */}
                      {message.mealId && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className="opacity-0 group-hover:opacity-100 transition-opacity p-2 hover:bg-muted rounded-lg"
                              data-testid={`meal-options-${message.mealId}`}
                            >
                              <MoreVertical className="w-4 h-4 text-muted-foreground" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            {message.foods && message.foods.length > 1 && (
                              <>
                                <DropdownMenuItem 
                                  onClick={() => openDeleteFoodDialog(message.mealId, message.foods)}
                                  className="cursor-pointer"
                                  data-testid={`delete-food-option-${message.mealId}`}
                                >
                                  <MinusCircle className="w-4 h-4 mr-2" />
                                  Eliminar un alimento
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                              </>
                            )}
                            <DropdownMenuItem 
                              onClick={() => deleteMeal(message.mealId)}
                              className="text-destructive focus:text-destructive cursor-pointer"
                              data-testid={`delete-meal-option-${message.mealId}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Eliminar comida completa
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                    {message.ai_comment && (
                      <p className="mt-2 text-xs text-muted-foreground italic">{message.ai_comment}</p>
                    )}
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex justify-start"
            >
              <div className="chat-bubble-ai px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                  <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: "0.2s" }} />
                  <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: "0.4s" }} />
                </div>
              </div>
            </motion.div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Delete Day Confirmation Dialog */}
        <AlertDialog open={deleteDayDialogOpen} onOpenChange={setDeleteDayDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar todo el día?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción eliminará todas las comidas registradas hoy. 
                Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={deleteEntireDay}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid="confirm-delete-day-btn"
              >
                Eliminar todo
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Specific Food Dialog */}
        <AlertDialog open={deleteFoodDialog.open} onOpenChange={(open) => !open && setDeleteFoodDialog({ open: false, mealId: null, foods: [] })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Selecciona el alimento a eliminar</AlertDialogTitle>
              <AlertDialogDescription>
                Elige el alimento que quieres eliminar de esta comida. Los totales se recalcularán automáticamente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4 space-y-2">
              {deleteFoodDialog.foods.map((food, index) => (
                <button
                  key={index}
                  onClick={() => deleteSpecificFood(deleteFoodDialog.mealId, index)}
                  className="w-full flex items-center justify-between p-3 rounded-xl bg-muted/50 hover:bg-destructive/10 hover:text-destructive transition-colors text-left"
                  data-testid={`delete-food-item-${index}`}
                >
                  <span className="font-medium">{food.name}</span>
                  <span className="text-sm text-muted-foreground">{Math.round(food.calories)} kcal</span>
                </button>
              ))}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Image Preview Modal */}
        <AnimatePresence>
          {showImageUpload && imagePreview && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-card border border-border rounded-2xl p-6 max-w-md w-full shadow-xl"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-semibold">Analizar foto de comida</h3>
                  <button 
                    onClick={cancelImageUpload}
                    className="p-1 hover:bg-muted rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  className="w-full rounded-xl mb-4 max-h-[300px] object-cover"
                />
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={cancelImageUpload}
                    className="flex-1 rounded-xl"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={sendImage}
                    disabled={loading}
                    className="flex-1 rounded-xl"
                    data-testid="send-image-btn"
                  >
                    {loading ? "Analizando..." : "Analizar"}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Input Area */}
        <div className="p-4 glass rounded-t-2xl border-t border-border/50">
          <form onSubmit={handleSubmit} className="flex items-center gap-2">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleImageSelect}
              className="hidden"
            />
            
            {/* Camera button */}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-12 w-12 rounded-xl shrink-0"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || isRecording}
              data-testid="camera-btn"
            >
              <Camera className="w-5 h-5" />
            </Button>

            {/* Mic button */}
            <Button
              type="button"
              variant={isRecording ? "destructive" : "ghost"}
              size="icon"
              className={`h-12 w-12 rounded-xl shrink-0 ${isRecording ? 'animate-pulse' : ''}`}
              onClick={isRecording ? stopRecording : startRecording}
              disabled={loading}
              data-testid="mic-btn"
            >
              {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </Button>

            {/* Text input */}
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={isRecording ? "Grabando..." : "Escribe lo que has comido..."}
              className="flex-1 h-12 px-4 bg-background rounded-xl border border-border/50 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none"
              disabled={loading || isRecording}
              data-testid="chat-input"
            />
            
            {/* Send button */}
            <Button
              type="submit"
              size="icon"
              className="h-12 w-12 rounded-xl shrink-0"
              disabled={loading || !input.trim() || isRecording}
              data-testid="chat-submit-btn"
            >
              <Send className="w-5 h-5" />
            </Button>
          </form>
          
          {/* Recording indicator */}
          {isRecording && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-center gap-2 mt-3 text-sm text-destructive"
            >
              <div className="w-2 h-2 bg-destructive rounded-full animate-pulse" />
              <span>Grabando... Pulsa el micrófono para enviar</span>
            </motion.div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default ChatPage;
